<template>
    <div class="data-table__container">
        <table class="data-table">
            <thead class="data-table__header">
                <tr class="data-table__row">
                    <th
                        v-for="(header, index) in headers"
                        :key="index"
                        :class="[
                            'data-table__header-cell',
                            {
                                'data-table__header-cell--sortable': header.sortable,
                            },
                        ]"
                        @click="handleHeaderClick(header)"
                    >
                        <div class="data-table__header-cell-wrapper">
                            <span class="data-table__header-cell-title">
                                {{ header.label }}
                            </span>

                            <span
                                v-if="header.sortable && sortDirections[header.key] === 'asc'"
                                class="sort-indicator"
                            >
                                <img v-svg-inline src="@/assets/icons/ui/sort-down.svg" />
                            </span>
                            <span
                                v-else-if="header.sortable && sortDirections[header.key] === 'desc'"
                                class="sort-indicator"
                            >
                                <img v-svg-inline src="@/assets/icons/ui/sort-up.svg" />
                            </span>
                            <span
                                v-else-if="header.sortable && sortDirections[header.key] === 'sort'"
                                class="sort-indicator"
                            >
                                <img v-svg-inline src="@/assets/icons/ui/sort.svg" />
                            </span>
                        </div>
                    </th>

                    <th
                        v-if="hasActions"
                        class="data-table__header-cell data-table__header-cell--actions"
                    ></th>
                </tr>
            </thead>

            <tbody class="data-table__body">
                <tr
                    v-for="(row, index) in data"
                    :key="index"
                    class="data-table__row"
                    @click="handleRowClick(row)"
                >
                    <td
                        v-for="(header, key) in headers"
                        :key="key"
                        class="data-table__cell"
                    >
                        <slot
                            :name="header.key"
                            :row="row"
                            :row-index="index"
                        >
                            {{ row[header.key] }}
                        </slot>
                    </td>

                    <td
                        v-if="hasActions"
                        class="data-table__cell data-table__cell--actions"
                    >
                        <slot name="actions" :row="row"></slot>
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</template>

<script lang="ts" setup>
    import { ref, reactive, onMounted, defineProps, defineEmits } from 'vue';

    const props = defineProps<{
        headers: { label: string, key: string, sortable?: boolean }[],
        data: Record<string, any>[],
        hasActions?: boolean,
        sortKey?: string,
        sortOrder?: string,
    }>();

    const emit = defineEmits(['row-click', 'sortable']);

    const sortKey = ref(props.sortKey ?? '');
    const sortOrder = ref<'sort' | 'asc' | 'desc'>(props.sortOrder as ('sort' | 'asc' | 'desc') ?? 'sort');
    const sortDirections = reactive<Record<string, 'sort' | 'asc' | 'desc'>>({});

    function handleRowClick(row: Record<string, any>): void {
        emit('row-click', row);
    }

    function handleHeaderClick(header: { label: string, key: string, sortable?: boolean }): void {
        if (!header.sortable) {
            return;
        }

        const current = sortDirections[header.key];
        let newDirection: 'sort' | 'asc' | 'desc';

        if (current === 'sort') {
            newDirection = 'asc';
        }
        else if (current === 'asc') {
            newDirection = 'desc';
        }
        else {
            newDirection = 'sort';
        }

        sortDirections[header.key] = newDirection;
        sortKey.value = header.key;
        sortOrder.value = newDirection;

        emit('sortable', header.key, newDirection === 'sort' ? undefined : newDirection);
    }

    onMounted(() => {
        props.headers.forEach((header) => {
            if (header.sortable) {
                sortDirections[header.key] = 'sort';
            }
        });

        if (sortKey.value && sortOrder.value) {
            sortDirections[sortKey.value] = sortOrder.value;
        }
    });
</script>

<style lang="scss">
    @use './DataTable.scss' as *;
</style>
